import React from "react";
import { LButton } from "@loffa/gpp-modules";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {faSave, faExclamationTriangle} from "@fortawesome/free-solid-svg-icons";
import { useConfirmationValidation } from "./hooks/useConfirmationValidation";
import { ConfirmationStepProps } from "./types/confirmationTypes";
import { ValidationSummary } from "./Components/ValidationSummary";
import { WarningModal } from "./Components/helperComponents";
import { GeneralInfoSection, SourceFileSection, ValidationsSection, PgpDecryptionSection, OutputDestinationsSection, NotificationsSection} from "./Components/sectionComponents";
import styles from "./Confirmation.module.css";
import { useAutoImportFormContext } from "../../../Context/AutoImportFormContext";

const Confirmation = ({ onSave }: ConfirmationStepProps) => {
  const { formData, error } = useAutoImportFormContext();
  const { isGeneralComplete, isSourceFileComplete, isValidationsComplete, isPgpDecryptionComplete, canSave, incompleteSections, showOverrideWarning, setShowOverrideWarning,} = useConfirmationValidation(formData);
  const {outputDestinations = [], notifications = []} = formData;

  const handleSave = () => {
    if (canSave) {
      onSave(formData);
    } else {
      setShowOverrideWarning(true);
    }
  };

  const handleOverrideSave = () => {
    onSave(formData);
  };

  return (
    <div className={styles.confirmationContainer}>
      <div className={styles.confirmationHeader}>
        <h2>Review and Confirm</h2>
        <p>
          Please review the information below before saving your Auto Import
          Configuration.
        </p>
      </div>

      <ValidationSummary
        isGeneralComplete={isGeneralComplete}
        isSourceFileComplete={isSourceFileComplete}
        isValidationsComplete={isValidationsComplete}
        isPgpDecryptionComplete={isPgpDecryptionComplete}
        outputDestinationsCount={outputDestinations?.length || 0}
        notificationsCount={notifications?.length || 0}
        canSave={canSave}
        showOverrideWarning={showOverrideWarning}
      />

      <div className={styles.confirmationContent}>
        <GeneralInfoSection formData={formData} />
        <SourceFileSection formData={formData} />
        <ValidationsSection formData={formData} />
        <PgpDecryptionSection formData={formData} />
        <OutputDestinationsSection outputDestinations={outputDestinations || []}/>
        <NotificationsSection notifications={notifications || []} />
      </div>

      {/*error*/}
      {error && (
        <div className={styles.errorMessage}>
          <FontAwesomeIcon
            icon={faExclamationTriangle}
            className={styles.errorIcon}
          />
          <span>{error}</span>
        </div>
      )}

      {/*warning ui for incomplete form(s)*/}
      <WarningModal
        incompleteSections={incompleteSections}
        onCancel={() => setShowOverrideWarning(false)}
        onSaveAnyway={handleOverrideSave}
        isVisible={showOverrideWarning && !canSave}
      />

      {/*save btn*/}
      <div className={styles.confirmationActions}>
        <LButton
          onClick={handleSave}
          themeColor="primary"
          className={styles.saveButton}
          disabled={false}
        >
          <FontAwesomeIcon icon={faSave} className={styles.buttonIcon} />
          Save Configuration
        </LButton>
        {!canSave && !showOverrideWarning && (
          <span className={styles.saveHint}>
            Some required fields are incomplete - click to review
          </span>
        )}
      </div>
    </div>
  );
}

export { Confirmation };
